export const NOTE_TYPE_GROUPS = [
    {
        group: 'Progress note',
        items: [
            { type: 'Individual', hint: '90834 · 90837' },
            { type: 'Crisis', hint: '90839 · 90840' },
        ],
    },
    {
        group: 'Couples and family notes', // ehr#689: the note's name, as V15
        items: [
            { type: 'Family', hint: '90847' },
            { type: 'Couples', hint: '90847' },
        ],
    },
    { group: 'Group note', items: [{ type: 'Group', hint: 'Start from the group appointment' }] },
    {
        group: 'Other notes',
        items: [
            { type: 'Case management', hint: 'H0006' },
            { type: 'Peer support', hint: 'H0038' },
            { type: 'Skills / rehab', hint: 'H2014' },
            { type: 'Collateral contact', hint: '90846' },
            { type: 'New Client', hint: '90791' },
            { type: 'Medication management', hint: '99213 · 99214' },
            { type: 'Treatment plan review', hint: 'No billing' },
            { type: 'Discharge summary', hint: 'No billing' },
            { type: 'Psych testing', hint: '96130 · 96136' },
            { type: 'No-show / cancellation', hint: 'No billing' },
            { type: 'Admin / phone', hint: 'No billing' },
        ],
    },
];
const ALL_TYPES = NOTE_TYPE_GROUPS.flatMap((g) => g.items.map((i) => i.type));
/**
 * ehr#708 / ehr#713: the kinds the picker marks "No billing" (Treatment plan review, Discharge
 * summary, No-show / cancellation, Admin / phone). They never carry a billing code: the editor
 * shows a read-only "No billing" box, the server drops a code sent with one, and
 * noteBillingSummary hands RCM no code for one (so a code saved before this never bills).
 */
export const NO_BILLING_KINDS = NOTE_TYPE_GROUPS.flatMap((g) => g.items.filter((i) => i.hint === 'No billing').map((i) => i.type));
export const isNoBillingKind = (kind) => typeof kind === 'string' && NO_BILLING_KINDS.includes(kind);
/** The billing keys of a note's session (the code, its hand pick and its add-ons). */
const BILLING_SESSION_KEYS = ['code', 'codePick', 'modifier', 'complexity', 'icReason'];
/**
 * ehr#708: a note body with its billing code taken off when its kind is a No billing one (the
 * body's noteType, else its session label as noteTypeOf reads it). Any other body comes back as
 * it is. The API runs it as a note is saved, reviewed and signed.
 */
export function withoutNoBillingCode(body, n = {}) {
    const kind = noteTypeOf({
        isGroup: n.isGroup,
        sessionLabel: n.sessionLabel,
        noteType: body.noteType,
    });
    const s = body.session;
    if (!isNoBillingKind(kind) || !s || typeof s !== 'object')
        return body;
    const session = { ...s };
    let changed = false;
    for (const k of BILLING_SESSION_KEYS)
        if (k in session) {
            delete session[k];
            changed = true;
        }
    return changed ? { ...body, session } : body;
}
/** ehr#864: the session type a group note is written as (the practice's appointment types read alike). */
export const GROUP_SESSION_TYPE = 'Group Therapy';
/** ehr#864: a group note's session type: its own when it already names a group, else Group Therapy. */
export function groupSessionType(isGroup, type) {
    const t = typeof type === 'string' ? type : undefined;
    if (!isGroup)
        return t;
    return t && /group/i.test(t) ? t : GROUP_SESSION_TYPE;
}
/**
 * ehr#864: a group note's body always says it was a group session. A group member's note saved
 * from the chart carried the editor's "Individual" default while it billed 90853, so the API writes
 * Group Therapy over any session type that doesn't name a group. A body with no session details,
 * or any other note's body, comes back as it is.
 */
export function withGroupSessionType(body, isGroup) {
    const s = body.session;
    if (!isGroup || !s || typeof s !== 'object')
        return body;
    const session = s;
    const type = groupSessionType(true, session.type);
    return type === session.type ? body : { ...body, session: { ...session, type } };
}
/**
 * A saved note's kind: the one it was started as (body.noteType); else from the note itself: a
 * group note is Group; otherwise its session type (the practice's appointment type, e.g. "Family
 * Therapy") names it; anything else is Individual.
 */
export function noteTypeOf(n) {
    if (n.isGroup)
        return 'Group';
    if (typeof n.noteType === 'string' && ALL_TYPES.includes(n.noteType))
        return n.noteType;
    const s = (n.sessionLabel ?? '').toLowerCase();
    // A session type named like a kind ("Case management", "Peer support") is that kind.
    const exact = ALL_TYPES.find((t) => t.toLowerCase() === s);
    if (exact)
        return exact;
    if (/crisis/.test(s))
        return 'Crisis';
    if (/couple|marital|marriage/.test(s))
        return 'Couples';
    if (/family/.test(s))
        return 'Family';
    if (/group/.test(s))
        return 'Group';
    if (/intake|assessment|evaluation|new client/.test(s))
        return 'New Client';
    return 'Individual';
}
/**
 * Case / service notes (V81 SERVICE_TYPES): billed from the note itself (H0006, H0038 and H2014 in
 * units, 90846), so RCM's auto claim takes the note's code and units over the appointment's CPT.
 */
export const SERVICE_NOTE_KINDS = [
    'Case management',
    'Peer support',
    'Skills / rehab',
    'Collateral contact',
];
/**
 * The lines in a note's saved billing code ("90834", "H0038 x2", "90839 + 90840 x2"), each with
 * its units (1 unless "x<n>" follows). Anything that isn't a code is left out.
 */
export function noteCodeLines(code) {
    if (typeof code !== 'string')
        return [];
    return code
        .split('+')
        .map((part) => /^\s*([A-Z]?\d{4,5}[A-Z]?)\s*(?:x\s*(\d{1,2}))?\s*$/i.exec(part))
        .filter((m) => Boolean(m))
        .map((m) => ({ code: m[1].toUpperCase(), units: m[2] ? Number(m[2]) : 1 }))
        .filter((l) => l.units > 0);
}
export function noteBillingSummary(n) {
    const b = n.body ?? {};
    const s = (b.session && typeof b.session === 'object' ? b.session : {});
    const kind = noteTypeOf({
        isGroup: n.isGroup,
        sessionLabel: n.sessionLabel,
        noteType: b.noteType,
    });
    // ehr#708: a No billing kind bills nothing, even with a code saved before the editor hid it
    const noBilling = isNoBillingKind(kind);
    const code = !noBilling && typeof s.code === 'string' ? s.code.trim() : '';
    const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
    // ehr#543: the saved modifier rides on the billing line (and on each of its codes)
    const modifier = code ? str(s.modifier) : null;
    const minutes = typeof s.durationMin === 'number' && Number.isFinite(s.durationMin) && s.durationMin > 0
        ? s.durationMin
        : null;
    return {
        kind,
        service: SERVICE_NOTE_KINDS.includes(kind),
        noBilling,
        code,
        lines: noteCodeLines(code).map((l) => (modifier ? { ...l, modifier } : l)),
        modifier,
        minutes,
        place: str(s.place),
        start: str(s.time),
        end: str(s.end),
    };
}
//# sourceMappingURL=noteKinds.js.map